import { apiClient } from "../core/apiClient";
import {
  PurchaseListRequest,
  PurchaseListResponse,
  AssignmentRequest,
  AssignmentResponse,
  QRGenerationRequest,
  QRGenerationResponse,
  StudentAvailableProductsResponse,
  ErrorResponse,
  AvailableProduct,
} from "../../types/purchaseList.types";

/**
 * Servicio para el módulo de Listas de Compra
 *
 * Maneja toda la comunicación con el backend para:
 * - Crear listas de compra (ProductCredit)
 * - Asignar productos a estudiantes
 * - Generar códigos QR
 * - Obtener productos disponibles por estudiante
 */
class PurchaseListService {
  private static readonly BASE_URL = "/api/v1";

  /**
   * Crea una nueva lista de compra (ProductCredit)
   * @param request - Datos para crear la lista de compra
   * @returns Promise con la lista de compra creada
   */
  static async createPurchaseList(
    request: PurchaseListRequest
  ): Promise<PurchaseListResponse> {
    try {
      const response = await apiClient.post<PurchaseListResponse>(
        `${this.BASE_URL}/purchase-lists/`,
        request
      );

      return response.data;
    } catch (error: any) {
      this.handleError(error, "Error al crear lista de compra");
      throw error;
    }
  }

  /**
   * Asigna productos a un estudiante
   * @param purchaseListId - ID de la lista de compra
   * @param request - Datos de la asignación
   * @returns Promise con la asignación creada
   */
  static async assignProductsToStudent(
    purchaseListId: string,
    request: AssignmentRequest
  ): Promise<AssignmentResponse> {
    try {
      const response = await apiClient.post<AssignmentResponse>(
        `${this.BASE_URL}/purchase-lists/${purchaseListId}/assign`,
        request
      );

      return response.data;
    } catch (error: any) {
      this.handleError(error, "Error al asignar productos al estudiante");
      throw error;
    }
  }

  /**
   * Genera un código QR para una lista de compra
   * @param purchaseListId - ID de la lista de compra
   * @param request - Datos para generar el QR
   * @returns Promise con el código QR generado
   */
  static async generateQR(
    purchaseListId: string,
    request: QRGenerationRequest
  ): Promise<QRGenerationResponse> {
    try {
      const response = await apiClient.post<QRGenerationResponse>(
        `${this.BASE_URL}/purchase-lists/${purchaseListId}/generate-qr`,
        request
      );

      return response.data;
    } catch (error: any) {
      this.handleError(error, "Error al generar código QR");
      throw error;
    }
  }

  /**
   * Obtiene los productos disponibles para un estudiante
   * @param studentId - ID del estudiante
   * @returns Promise con los productos disponibles
   */
  static async getStudentAvailableProducts(
    studentId: string
  ): Promise<StudentAvailableProductsResponse> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        message: string;
        data: StudentAvailableProductsResponse;
      }>(
        `${this.BASE_URL}/purchase-lists/students/${studentId}/available-products`
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(
        error,
        "Error al obtener productos disponibles del estudiante"
      );
      throw error;
    }
  }

  /**
   * Obtiene todos los productos de un estudiante (disponibles y entregados)
   * @param studentId - ID del estudiante
   * @returns Promise con todos los productos del estudiante
   */
  static async getStudentAllProducts(studentId: string): Promise<{
    studentId: string;
    studentName: string;
    allProducts: AvailableProduct[];
    availableProducts: AvailableProduct[];
    deliveredProducts: AvailableProduct[];
    totalAssigned: number;
    totalAvailable: number;
    totalDelivered: number;
  }> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/purchase-lists/students/${studentId}/all-products`
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(
        error,
        "Error al obtener todos los productos del estudiante"
      );
      throw error;
    }
  }

  /**
   * Obtiene todas las listas de compra de un apoderado
   * @param apoderadoId - ID del apoderado
   * @returns Promise con las listas de compra
   */
  static async getPurchaseListsByApoderado(
    // apoderadoId: string
  ): Promise<PurchaseListResponse[]> {
    try {
      // Por ahora, retornamos un array vacío ya que no existe un endpoint específico
      // para obtener listas de compra por apoderado
      console.log("⚠️ Endpoint no implementado: getPurchaseListsByApoderado");
      return [];
    } catch (error: any) {
      this.handleError(
        error,
        "Error al obtener listas de compra del apoderado"
      );
      throw error;
    }
  }

  /**
   * Obtiene una lista de compra por ID
   * @param purchaseListId - ID de la lista de compra
   * @returns Promise con la lista de compra
   */
  static async getPurchaseListById(
    purchaseListId: string
  ): Promise<PurchaseListResponse> {
    try {
      const response = await apiClient.get<PurchaseListResponse>(
        `${this.BASE_URL}/purchase-lists/${purchaseListId}`
      );

      return response.data;
    } catch (error: any) {
      this.handleError(error, "Error al obtener lista de compra");
      throw error;
    }
  }

  /**
   * Obtiene las asignaciones de un estudiante
   * @param studentId - ID del estudiante
   * @returns Promise con las asignaciones
   */
  static async getStudentAssignments(
    studentId: string
  ): Promise<AssignmentResponse[]> {
    try {
      const response = await apiClient.get<AssignmentResponse[]>(
        `${this.BASE_URL}/purchase-lists/students/${studentId}/assignments`
      );

      return response.data;
    } catch (error: any) {
      this.handleError(error, "Error al obtener asignaciones del estudiante");
      throw error;
    }
  }

  static async generateQRForMultipleProducts(data: {
    studentId: string;
    studentName: string;
    products: Array<{
      assignmentId: string;
      productId: string;
      productName: string;
      quantity: number;
    }>;
  }): Promise<{
    qrCode: string;
    totalProducts: number;
    totalQuantity: number;
  }> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/purchase-lists/generate-qr-multiple`,
        data
      );
      return response.data.data;
    } catch (error: any) {
      console.error("PurchaseListService Error:", error);
      throw error;
    }
  }

  static async processStudentRedemption(qrCode: string): Promise<{
    success: boolean;
    message: string;
    data?: {
      studentId: string;
      studentName: string;
      products: Array<{
        assignmentId: string;
        productId: string;
        productName: string;
        quantity: number;
        delivered: boolean;
      }>;
      totalProducts: number;
      totalQuantity: number;
      timestamp: string;
    };
    error?: string;
  }> {
    try {
      const endpoint = `${this.BASE_URL}/purchase-lists/process-redemption`;
      console.log('[PurchaseListService] Processing student redemption...');
      console.log('[PurchaseListService] Endpoint:', endpoint);
      console.log('[PurchaseListService] QR Code length:', qrCode.length);
      console.log('[PurchaseListService] QR Code preview:', qrCode.substring(0, 50) + '...');
      
      const response = await apiClient.post(
        endpoint,
        { qrCode }
      );
      
      console.log('[PurchaseListService] Response received:', response.status);
      console.log('[PurchaseListService] Response data:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error("PurchaseListService Error:", error);
      return {
        success: false,
        message: error.response?.data?.error || error.message || "Error procesando QR",
        error: error.response?.data?.error || error.message || "Error procesando QR"
      };
    }
  }

  /**
   * Maneja errores de la API de manera consistente
   * @param error - Error capturado
   * @param defaultMessage - Mensaje por defecto
   */
  private static handleError(error: any, _defaultMessage: string): void {
    console.error("PurchaseListService Error:", error);

    // Si es un error de respuesta del servidor
    if (error.response?.data) {
      const errorData = error.response.data as ErrorResponse;
      console.error("Server Error:", errorData.message, errorData.error);
    }

    // Si es un error de red
    if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
      console.error("Network Error:", error.message);
    }
  }

  /**
   * Verifica si un error es de autenticación
   * @param error - Error a verificar
   * @returns true si es un error de autenticación
   */
  static isAuthError(error: any): boolean {
    return error.response?.status === 401 || error.response?.status === 403;
  }

  /**
   * Verifica si un error es de validación
   * @param error - Error a verificar
   * @returns true si es un error de validación
   */
  static isValidationError(error: any): boolean {
    return error.response?.status === 400;
  }

  /**
   * Verifica si un error es de servidor
   * @param error - Error a verificar
   * @returns true si es un error de servidor
   */
  static isServerError(error: any): boolean {
    return error.response?.status >= 500;
  }

  /**
   * Obtiene el mensaje de error legible
   * @param error - Error a procesar
   * @param defaultMessage - Mensaje por defecto
   * @returns Mensaje de error legible
   */
  static getErrorMessage(error: any, defaultMessage: string): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.message) {
      return error.message;
    }

    return defaultMessage;
  }
}

export default PurchaseListService;
